/**
 * CommentItem Component
 * Displays a single comment with attachments and actions
 */

import { useState } from 'react'
import {
  User,
  Clock,
  Lock,
  Trash2,
  Edit2,
  Check,
  X,
  Paperclip,
  Download,
  FileText,
  Eye
} from 'lucide-react'
import { format } from 'date-fns'
import type { CommentWithAttachments, CommentAttachment } from '../../types/comment'

interface CommentItemProps {
  comment: CommentWithAttachments
  currentUserRole: 'admin' | 'subcontractor'
  currentUserId: string
  onUpdate: (commentId: string, content: string) => Promise<void>
  onDelete: (commentId: string) => Promise<void>
  onDeleteAttachment: (attachmentId: string) => Promise<void>
}

export function CommentItem({
  comment,
  currentUserRole,
  currentUserId,
  onUpdate,
  onDelete,
  onDeleteAttachment
}: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)

  const isOwner = comment.user_id === currentUserId
  const canEdit = isOwner
  const canDelete = isOwner || currentUserRole === 'admin'

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return
    setIsSaving(true)
    try {
      await onUpdate(comment.id, editContent)
      setIsEditing(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditContent(comment.content)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(comment.id)
    } finally {
      setIsDeleting(false)
    }
  }

  const isImageFile = (fileType: string) => {
    return fileType.startsWith('image/')
  }

  // Format content with @mentions styled as blue links
  const formatContentWithMentions = (content: string) => {
    // Match @"Name" or @Name patterns
    const mentionRegex = /@"([^"]+)"|@(\w+)/g
    const parts: (string | JSX.Element)[] = []
    let lastIndex = 0
    let match

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index))
      }

      // Get the name (either from quotes or without)
      const name = match[1] || match[2]

      // Add the styled mention
      parts.push(
        <span
          key={match.index}
          className="text-primary-600 dark:text-primary-400 font-medium"
        >
          {name}
        </span>
      )

      lastIndex = match.index + match[0].length
    }

    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex))
    }

    return parts.length > 0 ? parts : content
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const renderAttachment = (attachment: CommentAttachment) => {
    const isImage = isImageFile(attachment.file_type)

    return (
      <div
        key={attachment.id}
        className="flex items-center gap-2 p-2 bg-nature-100 dark:bg-nature-700 rounded-lg group"
      >
        {isImage && attachment.url ? (
          <button
            onClick={() => setPreviewImage(attachment.url || null)}
            className="relative w-16 h-16 rounded overflow-hidden hover:opacity-80 transition-opacity"
          >
            <img
              src={attachment.url}
              alt={attachment.file_name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
              <Eye className="h-4 w-4 text-white" />
            </div>
          </button>
        ) : (
          <div className="w-10 h-10 rounded bg-nature-200 dark:bg-nature-600 flex items-center justify-center">
            <FileText className="h-5 w-5 text-nature-500 dark:text-nature-400" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
            {attachment.file_name}
          </p>
          <p className="text-xs text-nature-500">
            {formatFileSize(attachment.file_size)}
          </p>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {attachment.url && (
            <a
              href={attachment.url}
              download={attachment.file_name}
              className="p-1.5 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300"
              title="Ladda ner"
            >
              <Download className="h-4 w-4" />
            </a>
          )}
          {canDelete && (
            <button
              onClick={() => onDeleteAttachment(attachment.id)}
              className="p-1.5 text-red-500 hover:text-red-700"
              title="Ta bort"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={`
        p-4 rounded-lg border
        ${comment.is_internal
          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
          : 'bg-white dark:bg-nature-800 border-nature-200 dark:border-nature-700'
        }
      `}>
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center
              ${comment.user_role === 'admin'
                ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400'
                : 'bg-nature-100 dark:bg-nature-700 text-nature-600 dark:text-nature-400'
              }
            `}>
              <User className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-nature-900 dark:text-nature-100">
                  {comment.user_name}
                </span>
                {comment.is_internal && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded">
                    <Lock className="h-3 w-3" />
                    Intern
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-nature-500">
                <Clock className="h-3 w-3" />
                <span>
                  {format(new Date(comment.created_at), "yyyy-MM-dd 'kl.' HH:mm")}
                </span>
                {comment.updated_at !== comment.created_at && (
                  <span className="italic">(redigerad)</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          {(canEdit || canDelete) && !isEditing && (
            <div className="flex items-center gap-1">
              {canEdit && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1.5 text-nature-500 hover:text-nature-700 dark:hover:text-nature-300 transition-colors"
                  title="Redigera"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="p-1.5 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                  title="Ta bort"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="mt-2">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="w-full p-3 rounded-lg border border-nature-300 dark:border-nature-600 bg-white dark:bg-nature-900 text-nature-900 dark:text-nature-100 resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1.5 text-sm text-nature-600 dark:text-nature-400 hover:text-nature-800 dark:hover:text-nature-200 transition-colors"
              >
                <X className="h-4 w-4 inline mr-1" />
                Avbryt
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSaving || !editContent.trim()}
                className="px-3 py-1.5 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                <Check className="h-4 w-4 inline mr-1" />
                Spara
              </button>
            </div>
          </div>
        ) : (
          <p className="text-nature-700 dark:text-nature-300 whitespace-pre-wrap">
            {formatContentWithMentions(comment.content)}
          </p>
        )}

        {/* Attachments */}
        {comment.attachments && comment.attachments.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-1 text-xs text-nature-500">
              <Paperclip className="h-3 w-3" />
              <span>{comment.attachments.length} {comment.attachments.length === 1 ? 'bilaga' : 'bilagor'}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {comment.attachments.map(renderAttachment)}
            </div>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewImage(null)}
        >
          <button
            onClick={() => setPreviewImage(null)}
            className="absolute top-4 right-4 p-2 text-white hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={previewImage}
            alt="Förhandsgranskning"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  )
}
